/**
 * Desktop shell — Encapsulates the entire desktop UI (sidebar with nav links,
 * resize handle, main content). Pure render component driven by shell props.
 */
import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Bell, PushPin, PushPinSlash, CaretRight, Gear, SignOut, ArrowsClockwise,
} from '@phosphor-icons/react';
import { NotificationPanel } from '../layout/LayoutParts';
import { TimeModificationsAlert } from '../TimeModificationsAlert';
import { ProfilePhotoGate } from '../ProfilePhotoGate';

const COLLAPSED_WIDTH = 72;

const DesktopShell = ({
  children,
  envBadge,
  environment,
  user,
  settings,
  t,
  checkAuth,
  location,
  navigate,
  // sidebar
  sidebarRef,
  autoCollapse,
  togglePin,
  isHoveringSidebar,
  sidebarWidth,
  // notifications
  notifRef,
  notifications,
  unreadCount,
  showNotifPanel,
  setShowNotifPanel,
  fetchNotifications,
  markAllRead,
  handleNotifClick,
  // nav
  orderedLinks,
  navBadges,
  gmailUnread,
  markNavSeen,
  expandedGroups,
  toggleGroup,
  reorderMode,
  startLongPress,
  cancelLongPress,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  touchOpenFlyout,
  setTouchOpenFlyout,
  // profile
  profileMenuRef,
  showProfileMenu,
  setShowProfileMenu,
  handleLogout,
  // resize handle
  isResizing,
  startResize,
}) => {
  const isCollapsed = autoCollapse && !isHoveringSidebar;
  const effectiveWidth = isCollapsed ? COLLAPSED_WIDTH : sidebarWidth;

  return (
    <>
      {envBadge}
      <div className="app-layout" style={{ paddingTop: environment !== 'production' ? '24px' : 0 }}>
        <aside
          className={`sidebar ${autoCollapse ? 'collapsed' : ''} ${isHoveringSidebar ? 'hovering' : ''}`}
          data-testid="sidebar-menu"
          ref={sidebarRef}
          style={{ width: `${effectiveWidth}px` }}
        >
          <div className="sidebar-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" style={{ height: '36px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <img src="/batiboys-logo.svg" alt="BATI-BOYS" style={{ height: '36px', objectFit: 'contain' }} />
                )}
                <span className="sidebar-logo" style={{ color: settings.primary_color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {settings.company_name}
                </span>
              </div>
              <div ref={notifRef} style={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }} className="bell-wrapper">
                <button
                  type="button"
                  className={`sidebar-pin-btn ${!autoCollapse ? 'pinned' : ''}`}
                  onClick={togglePin}
                  title={autoCollapse ? 'Epingler la sidebar (toujours visible)' : 'Desepingler (retractable au survol)'}
                  data-testid="sidebar-pin-btn"
                >
                  {autoCollapse ? <PushPinSlash size={18} /> : <PushPin size={18} weight="fill" />}
                </button>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  title="Rafraichir la page (F5)"
                  data-testid="refresh-page-btn"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center', color: '#5E5F66', transition: 'background-color 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <ArrowsClockwise size={20} />
                </button>
                <button
                  data-testid="notification-bell"
                  onClick={() => { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel) fetchNotifications(); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center', transition: 'background-color 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <Bell size={24} weight={unreadCount > 0 ? 'fill' : 'regular'} style={{ color: unreadCount > 0 ? settings.primary_color : '#5E5F66' }} />
                  {unreadCount > 0 && (
                    <span data-testid="notification-badge" style={{
                      position: 'absolute', top: '2px', right: '2px', backgroundColor: '#C62828', color: 'white',
                      borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.65rem', fontWeight: '700',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white',
                    }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </button>
                {showNotifPanel && (
                  <NotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    primaryColor={settings.primary_color}
                    t={t}
                    onMarkAllRead={markAllRead}
                    onClose={() => setShowNotifPanel(false)}
                    onNotifClick={handleNotifClick}
                    style={{ position: 'fixed', top: '60px', left: `${effectiveWidth + 8}px`, width: '360px' }}
                  />
                )}
              </div>
            </div>
          </div>

          <nav className={`sidebar-nav ${reorderMode ? 'nav-reorder-mode' : ''}`}>
            {reorderMode && (
              <div data-testid="nav-reorder-banner" style={{ padding: '8px 14px', margin: '8px 12px 12px', backgroundColor: '#FFF3E0', border: '1px dashed #EF6C00', borderRadius: '8px', fontSize: '0.78rem', color: '#E65100', textAlign: 'center', fontWeight: 600 }}>
                Glissez pour reorganiser • cliquez ailleurs pour terminer
              </div>
            )}
            {orderedLinks.map((link) => {
              const badge = link.to === '/gmail' ? gmailUnread : (navBadges[link.to] || 0);
              const hasChildren = Array.isArray(link.children) && link.children.length > 0;
              const isExpanded = !!expandedGroups[link.to];
              const activeChild = hasChildren && (location.pathname + location.search).startsWith(link.to)
                ? link.children.find((c) => (location.pathname + location.search) === c.to)
                : null;
              return (
                <React.Fragment key={link.to}>
                  <div
                    className={`nav-item-wrapper ${touchOpenFlyout === link.to ? 'flyout-open-touch' : ''} ${reorderMode ? 'nav-reorderable' : ''}`}
                    style={{ position: 'relative' }}
                    draggable={reorderMode}
                    onDragStart={(e) => handleDragStart(e, link.to)}
                    onDragOver={(e) => handleDragOver(e, link.to)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onMouseDown={() => startLongPress(link.to)}
                    onMouseUp={cancelLongPress}
                    onMouseLeave={() => { cancelLongPress(); }}
                    onTouchStart={() => startLongPress(link.to)}
                    onTouchEnd={cancelLongPress}
                    onTouchCancel={cancelLongPress}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      e.currentTarget.style.setProperty('--flyout-top', `${rect.top}px`);
                    }}
                  >
                    <NavLink
                      to={link.to}
                      onClick={(e) => {
                        if (hasChildren && autoCollapse && !isHoveringSidebar) {
                          e.preventDefault();
                          const rect = e.currentTarget.closest('.nav-item-wrapper')?.getBoundingClientRect();
                          if (rect) e.currentTarget.closest('.nav-item-wrapper').style.setProperty('--flyout-top', `${rect.top}px`);
                          setTouchOpenFlyout(touchOpenFlyout === link.to ? null : link.to);
                          return;
                        }
                        if (hasChildren) {
                          e.preventDefault();
                          toggleGroup(link.to);
                          if (!isExpanded) navigate(link.children[0].to);
                        } else if (badge > 0) {
                          markNavSeen(link.to);
                        }
                      }}
                      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      end={link.to === '/'}
                      data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      style={({ isActive }) => ({
                        borderLeftColor: (isActive || (hasChildren && activeChild)) ? settings.primary_color : 'transparent',
                        color: (isActive || (hasChildren && activeChild)) ? settings.primary_color : undefined,
                        position: 'relative',
                      })}
                    >
                      <link.icon size={28} weight="regular" />
                      {hasChildren && (
                        <span
                          data-testid={`nav-has-children-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className="nav-children-dot"
                          title="Menu deroulant - cliquez pour ouvrir"
                        />
                      )}
                      <span style={{ flex: 1 }}>{link.label}</span>
                      {badge > 0 && (
                        <span
                          data-testid={link.to === '/gmail' ? 'gmail-unread-badge' : `nav-badge-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                          style={{
                            minWidth: '18px', height: '18px', borderRadius: '9px',
                            backgroundColor: '#C62828', color: 'white',
                            fontSize: '0.7rem', fontWeight: '700',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '0 5px', marginLeft: '6px', lineHeight: 1,
                          }}
                        >
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                      {hasChildren && (
                        <span style={{ marginLeft: '4px', display: 'inline-flex', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.6 }}>
                          <CaretRight size={14} weight="bold" />
                        </span>
                      )}
                    </NavLink>
                    {/* Flyout popover for collapsed sidebar */}
                    {autoCollapse && !isHoveringSidebar && (
                      <div
                        className="nav-flyout"
                        data-testid={`flyout-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        style={{ top: 'var(--flyout-top, 0)' }}
                        onMouseEnter={(e) => {
                          const wrapper = e.currentTarget.closest('.nav-item-wrapper');
                          if (wrapper) wrapper.classList.add('flyout-pinned');
                        }}
                        onMouseLeave={(e) => {
                          const wrapper = e.currentTarget.closest('.nav-item-wrapper');
                          if (wrapper) wrapper.classList.remove('flyout-pinned');
                        }}
                      >
                        <div className="nav-flyout-title">{link.label}</div>
                        {hasChildren && link.children.map((child) => {
                          const isChildActive = (location.pathname + location.search) === child.to;
                          return (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              onClick={() => setTouchOpenFlyout(null)}
                              className={`nav-flyout-item ${isChildActive ? 'active' : ''}`}
                            >
                              {child.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {hasChildren && isExpanded && (!autoCollapse || isHoveringSidebar) && (
                    <div className="nav-subitems" data-testid={`subnav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {link.children.map((child) => {
                        const isChildActive = (location.pathname + location.search) === child.to;
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            className={`nav-subitem ${isChildActive ? 'active' : ''}`}
                            style={{ color: isChildActive ? settings.primary_color : undefined, borderLeftColor: isChildActive ? settings.primary_color : 'transparent' }}
                          >
                            {child.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          <div className="sidebar-footer">
            <div ref={profileMenuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                data-testid="profile-menu-trigger"
                onClick={() => setShowProfileMenu((v) => !v)}
                className="user-info"
                style={{
                  width: '100%',
                  background: showProfileMenu ? '#F4F5F7' : 'transparent',
                  border: '1px solid transparent',
                  borderColor: showProfileMenu ? 'var(--border, #E2E2E6)' : 'transparent',
                  borderRadius: '10px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { if (!showProfileMenu) e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
                onMouseLeave={(e) => { if (!showProfileMenu) e.currentTarget.style.backgroundColor = 'transparent'; }}
                title="Profil"
              >
                <div className="user-avatar" style={{ backgroundColor: settings.primary_color }}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-details" style={{ flex: 1, minWidth: 0 }}>
                  <div className="user-name">{user?.name || 'Utilisateur'}</div>
                  <div className="user-role">{user?.role === 'admin' ? t('role.admin') : t('role.employee')}</div>
                </div>
                <CaretRight size={14} weight="bold" style={{ opacity: 0.5, transform: 'rotate(-90deg)', flexShrink: 0 }} />
              </button>

              {showProfileMenu && (
                <div
                  data-testid="profile-menu-dropdown"
                  style={{
                    position: 'fixed',
                    bottom: '12px',
                    left: `${effectiveWidth + 8}px`,
                    minWidth: '220px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border, #E2E2E6)',
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                    padding: '6px',
                    zIndex: 1000,
                  }}
                >
                  <button
                    type="button"
                    data-testid="profile-menu-settings"
                    onClick={() => { setShowProfileMenu(false); navigate('/settings'); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', background: 'transparent', border: 'none',
                      borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#1F2024',
                      textAlign: 'left', transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Gear size={18} weight="regular" />
                    <span>Paramètres</span>
                  </button>
                  <button
                    type="button"
                    data-testid="logout-btn"
                    onClick={() => { setShowProfileMenu(false); handleLogout(); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', background: 'transparent', border: 'none',
                      borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#C62828',
                      textAlign: 'left', transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FEEBEE'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <SignOut size={18} weight="bold" />
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>

        {!autoCollapse && (
          <div
            data-testid="sidebar-resize-handle"
            onMouseDown={startResize}
            style={{ position: 'fixed', top: 0, left: `${sidebarWidth - 3}px`, width: '6px', height: '100vh', cursor: 'col-resize', zIndex: 101, backgroundColor: 'transparent', transition: 'background-color 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,47,167,0.2)'; }}
            onMouseLeave={(e) => { if (!isResizing.current) e.currentTarget.style.backgroundColor = 'transparent'; }}
          />
        )}

        <main className="main-content" style={{ marginLeft: `${autoCollapse ? COLLAPSED_WIDTH : sidebarWidth}px`, transition: 'margin-left 0.22s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          {children}
        </main>
        <TimeModificationsAlert user={user} />
        <ProfilePhotoGate user={user} onUploaded={checkAuth} />
      </div>
    </>
  );
};

export default DesktopShell;
